import React, { useState, useMemo, useEffect } from 'react';
import { StyleSheet, View, Pressable } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore } from '@/store';
import SettingsSection from '@/components/SettingsSection';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import UserUpdateEmailForm from '@/components/Forms/UserUpdateEmailForm';
import UserUpdatePasswordForm from '@/components/Forms/UserUpdatePasswordForm';
import User2FAUpdateForm from '@/components/Forms/User2FAUpdateForm';
import ConnectedAccountsSettings from '@/components/Settings/ConnectedAccountsSettings';
import ProfileSettings from '@/components/Settings/ProfileSettings';
import { useModalHelper } from '@/hooks/useModalHelper';
import { useTranslation } from '@/hooks/useTranslation';

const createStyles = (colors) => StyleSheet.create({
  container: {
    flex: 1,
  },
  pillsWrapper: {
    marginBottom: 15,
    paddingBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: colors.border_color_secondary,
  },
  formContainer: {
    width: '100%',
    paddingTop: 8,
  },
});

function AccountSettings({ onSetHeaderActions, onDirtyChange, initialTab }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const hasPassword = useAuthStore(state => state.user?.hasPassword);
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [dirty, setDirty] = useState(false);
  const { useConfirmationModal } = useModalHelper();
  const confirm = useConfirmationModal();
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  const tabs = useMemo(() => ([
    { id: 'profile', label: t('settingsWeb.profile'), icon: 'user' },
    { id: 'email', label: t('settings.account.email'), icon: 'envelope' },
    { id: 'password', label: t('settings.account.password'), icon: 'lock' },
    { id: '2fa', label: t('settings.account.twoFA'), icon: 'shield-check' },
    { id: 'connected', label: t('settings.account.connectedAccounts'), icon: 'link' },
  ]), [t]);
  const [activeTab, setActiveTab] = useState(tabs.some(tab => tab.id === initialTab) ? initialTab : 'profile');

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    const change = () => {
      setActiveTab(tab);
      setDirty(false);
      if (tab !== 'profile') onSetHeaderActions?.([]);
    };
    if (dirty) {
      confirm({ title: t('settingsPage.unsavedTitle'), message: t('settingsPage.unsavedMessage'), confirmText: t('settingsPage.discard'), onConfirm: change });
    } else change();
  };

  return (
    <View style={styles.container}>
      <View style={[styles.pillsWrapper, { flexDirection: 'row', flexWrap: 'wrap', gap: 6 }]}>
        {tabs.map(tab => <Pressable
          key={tab.id}
          accessibilityRole="button"
          accessibilityState={{ selected: activeTab === tab.id }}
          onPress={() => handleTabChange(tab.id)}
          style={({ hovered, pressed }) => ({
            flexDirection: 'row', alignItems: 'center', gap: 7,
            minHeight: 40, paddingHorizontal: 12, borderRadius: 8,
            backgroundColor: activeTab === tab.id || hovered || pressed ? colors.solid_color_tertiary : 'transparent',
          })}
        >
          <Icon name={tab.icon} size={14} color={colors.text_color_secondary} />
          <Text size="s" weight={activeTab === tab.id ? 'medium' : 'regular'}>{tab.label}</Text>
        </Pressable>)}
      </View>

      {activeTab === 'profile' && (
        <ProfileSettings onSetHeaderActions={onSetHeaderActions} onDirtyChange={setDirty} />
      )}

      {activeTab === 'email' && (
        <SettingsSection
          title={t('settings.account.updateEmail')}
          subtitle={t('settings.account.updateEmailSubtitle')}
          isBold
          isColumn
          isFirst
          isLast
        >
          <View style={styles.formContainer}>
            <UserUpdateEmailForm/>
          </View>
        </SettingsSection>
      )}

      {activeTab === 'password' && (
         <SettingsSection
          title={hasPassword ? t('settings.account.changePassword') : t('settings.account.setPassword')}
          subtitle={hasPassword ? t('settings.account.updatePassword') : t('settings.account.setPasswordSubtitle')}
          isBold
          isColumn
          isFirst
          isLast
        >
          <View style={styles.formContainer}>
            <UserUpdatePasswordForm />
          </View>
        </SettingsSection>
      )}

      {activeTab === '2fa' && (
        <SettingsSection
          title={t('settings.account.twoFactorTitle')}
          subtitle={t('settings.account.twoFactorSubtitle')}
          isBold
          isColumn
          isFirst
          isLast
        >
          <View style={styles.formContainer}>
            <User2FAUpdateForm />
          </View>
        </SettingsSection>
      )}

      {activeTab === 'connected' && (
        <ConnectedAccountsSettings />
      )}
    </View>
  );
} 

export default React.memo(AccountSettings);